import React, { useState } from 'react';
import {
  Icon,
  Typography,
  SelectMenu,
  Tag,
  Spinner,
  Tooltip,
  Button,
  Modal,
  TextField,
} from '@scale/scaleui';
import {
  useSignupFlowsQuery,
  useFetchSignupFlowQuery,
  useSignupFlowWorkerSkillQuery,
  useJobPostsForSignupFlowQuery,
} from '../../api';
import apiFetch, { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import CopyToClipboard from 'react-copy-to-clipboard';
import PanelEntry from '../PanelEntry';
import { toast } from 'react-toastify';
import SignupFlowCountrySelect from '../signupFlow/SignupFlowCountrySelect';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { notifyCopySuccess } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/ConfigComponents';

interface DetailPanelSignupFlowProps {
  ocpFlowId?: string;
}

// eslint-disable-next-line complexity
const DetailPanelSignupFlow: React.FC<DetailPanelSignupFlowProps> = ({ ocpFlowId }) => {
  const { signupFlowId, setSignupFlowId } = useOCPStore(state => ({
    signupFlowId: state.selectedSignupFlowId,
    setSignupFlowId: state.setSelectedSignupFlowId,
  }));

  const [manualUserId, setManualUserId] = useState<string>('');
  const [isEmailWhitelistModalOpen, setIsEmailWhitelistModalOpen] = useState(false);

  const signupFlowsQuery = useSignupFlowsQuery(ocpFlowId);
  const signupFlowQuery = useFetchSignupFlowQuery(signupFlowId);
  const selectedSignupFlow = signupFlowQuery.data?.signupFlow;
  const jobPostsQuery = useJobPostsForSignupFlowQuery(signupFlowId);
  const activeJobPosts = jobPostsQuery.data?.filter(jobPost => jobPost.isActive) || [];
  const workerSkillQuery = useSignupFlowWorkerSkillQuery(selectedSignupFlow);

  const hasWhitelist =
    selectedSignupFlow?.requiresWhitelistedEmail ||
    selectedSignupFlow?.useWhitelistRegex ||
    (selectedSignupFlow?.whitelistGreenhouseJobIds &&
      !!selectedSignupFlow?.whitelistGreenhouseJobIds.length);

  const manuallyApplyFlow = React.useCallback(async () => {
    if (!selectedSignupFlow || !manualUserId) return;
    try {
      await apiFetch(null, `corp-api/onboarding-control-panel/signup-flow/trigger`, {
        method: 'POST',
        body: {
          signupFlowToken: selectedSignupFlow.token,
          user: manualUserId,
        },
      });
      toast(`Successfully triggered signup flow for user ${manualUserId}`, {
        position: 'bottom-center',
        autoClose: 5000,
        progressStyle: { background: 'green' },
      });
      setManualUserId('');
    } catch (e) {
      toast(`Unable to apply signup flow to user ${manualUserId}`, {
        position: 'bottom-center',
        autoClose: 5000,
        progressStyle: { background: 'red' },
      });
      defaultApiFetchErrorHandler(e, { handleHttp: true });
    }
  }, [selectedSignupFlow, manualUserId]);

  return (
    <PanelEntry
      title={
        signupFlowsQuery.data?.length
          ? `Signup Flows (${signupFlowsQuery.data?.length} set up)`
          : 'Signup Flows'
      }
    >
      <SelectMenu
        className="my-1"
        options={
          (signupFlowsQuery.data?.length ?? 0) === 0
            ? [{ id: '', name: 'No Signup Flow (BPO or Manually Added)' }]
            : signupFlowsQuery.data?.map(flow => ({
                id: flow.id,
                name: flow.name,
                ...(flow.active
                  ? {}
                  : { tag: { variant: 'neutral', value: 'Inactive', size: 'sm' } }),
              })) ?? []
        }
        selectedOptionId={signupFlowId}
        setSelected={setSignupFlowId}
        placeholder="Choose a signup flow..."
      />
      {signupFlowId && !signupFlowQuery.data && (
        <div className="flex justify-center">
          <Spinner className="mt-2" size="sm" variant="primary" />
        </div>
      )}
      {signupFlowId && signupFlowQuery.data && selectedSignupFlow && (
        <>
          <div className="grid grid-cols-7 items-center justify-items-start gap-y-6">
            <Typography.Paragraph3 className="mt-3 col-span-2">
              Signup Flow ID
            </Typography.Paragraph3>
            <Typography.Paragraph3 className="mt-3 col-span-5">
              {selectedSignupFlow.token || "Error: couldn't find ID"}
            </Typography.Paragraph3>
            <Typography.Paragraph3 className="col-span-2 self-start mt-2">
              Contributor-Facing Link
            </Typography.Paragraph3>
            <div className="flex flex-col col-span-5">
              {activeJobPosts.length ? (
                activeJobPosts.map(jobPost => (
                  <div key={jobPost.id} className="flex items-center">
                    <a
                      href={`https://app.outlier.ai/en/expert/opportunities/${jobPost.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center"
                    >
                      <Typography.Paragraph3>{jobPost.name}</Typography.Paragraph3>
                      <Icon icon={['fas', 'external-link-alt']} variant="primary" />
                    </a>
                    <CopyToClipboard
                      text={`https://app.outlier.ai/en/expert/opportunities/${jobPost.id}`}
                      onCopy={notifyCopySuccess}
                    >
                      <Icon icon={['fas', 'copy']} rounded variant="primary" />
                    </CopyToClipboard>
                  </div>
                ))
              ) : (
                <div className="flex items-center">
                  <a
                    href={`https://app.outlier.ai/en/expert/signup?signup_flow=${selectedSignupFlow.token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon={['fas', 'external-link-alt']} variant="primary" />
                  </a>
                  <CopyToClipboard
                    text={`https://app.outlier.ai/en/expert/signup?signup_flow=${selectedSignupFlow.token}`}
                    onCopy={notifyCopySuccess}
                  >
                    <Icon icon={['fas', 'copy']} rounded variant="primary" />
                  </CopyToClipboard>
                </div>
              )}
            </div>
            <Typography.Paragraph3 className="col-span-2">Signup Flow Status</Typography.Paragraph3>
            <Tag className="col-span-5" variant={selectedSignupFlow.active ? 'success' : 'neutral'}>
              {selectedSignupFlow.active ? 'Active' : 'Inactive'}
            </Tag>
            {!!jobPostsQuery.data?.length && (
              <>
                <Typography.Paragraph3 className="col-span-2 self-start mt-2">
                  Connected Job Posts
                </Typography.Paragraph3>
                <div className="flex flex-col col-span-5">
                  {jobPostsQuery.isLoading && (
                    <Spinner className="mt-2" size="sm" variant="primary" />
                  )}
                  {jobPostsQuery.data?.map(jobPost => (
                    <div key={jobPost.id} className="flex items-center">
                      <a
                        href={`/corp${Routes.JobPostSetup}/${jobPost.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center"
                      >
                        <Typography.Paragraph3>{jobPost.name}</Typography.Paragraph3>
                        <Tag className="ml-2" variant={jobPost.isActive ? 'success' : 'neutral'}>
                          {jobPost.isActive ? 'Active' : 'Paused'}
                        </Tag>
                        <Icon icon={['fas', 'external-link-alt']} variant="primary" />
                      </a>
                    </div>
                  ))}
                </div>
              </>
            )}
            {selectedSignupFlow.workerSource && (
              <>
                <Typography.Paragraph3 className="col-span-2">Worker Source</Typography.Paragraph3>
                <Tag className="col-span-5">{selectedSignupFlow.workerSource}</Tag>
              </>
            )}
            {!!signupFlowQuery.data.tags.length && (
              <>
                <Typography.Paragraph3 className="col-span-2 self-start mt-1">
                  Tags Granted
                </Typography.Paragraph3>
                <div className="col-span-5 flex flex-wrap gap-1">
                  {signupFlowQuery.data.tags.map(tag => (
                    <Tag key={tag._id} variant="neutral">
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              </>
            )}
            {!!signupFlowQuery.data.intendedProjects?.length && (
              <>
                <Typography.Paragraph3 className="col-span-2 self-start mt-1">
                  Intended Projects
                </Typography.Paragraph3>
                <div className="col-span-5 flex flex-wrap gap-1">
                  {signupFlowQuery.data.intendedProjects.map(project => (
                    <Tag key={project._id} variant="neutral">
                      {project.name}
                    </Tag>
                  ))}
                </div>
              </>
            )}
            {selectedSignupFlow.workerSkillGrant && (
              <>
                <Typography.Paragraph3 className="col-span-2">
                  Worker Skill Grant
                </Typography.Paragraph3>
                {workerSkillQuery.data?.name ? (
                  <Tag
                    className="col-span-5"
                    variant="neutral"
                  >{`${workerSkillQuery.data.name} | ${selectedSignupFlow.workerSkillGrant.status}`}</Tag>
                ) : (
                  <Spinner className="col-span-5" size="xs" variant="primary" />
                )}
              </>
            )}
            <Typography.Paragraph3 className="col-span-2 self-start mt-1">
              Allowed Countries
            </Typography.Paragraph3>
            <SignupFlowCountrySelect
              className="col-span-5"
              isCountryChecked={(countryCode: string) =>
                selectedSignupFlow.allowlistCountryCodes?.includes(countryCode) || false
              }
              handleCountryCheck={() => {}}
              isDisabled={() => true}
            />
            {hasWhitelist && (
              <>
                <Typography.Paragraph3 className="col-span-2">Whitelists</Typography.Paragraph3>
                <div className="col-span-5 flex items-center gap-8">
                  {selectedSignupFlow.requiresWhitelistedEmail && (
                    <div className="flex items-center gap-2">
                      <Typography.Paragraph3>Emails</Typography.Paragraph3>
                      <Button
                        onClick={() => setIsEmailWhitelistModalOpen(true)}
                        variant="secondary"
                        size="sm"
                      >
                        View
                      </Button>
                      <Modal
                        open={isEmailWhitelistModalOpen}
                        onClose={() => setIsEmailWhitelistModalOpen(false)}
                        title="Email Whitelist"
                      >
                        {signupFlowQuery.data.emails.map(email => (
                          <div className="mb-1" key={email}>
                            {email}
                          </div>
                        ))}
                      </Modal>
                    </div>
                  )}
                  {selectedSignupFlow.useWhitelistRegex && selectedSignupFlow.whitelistRegex && (
                    <Tooltip
                      text={`Regex: ${selectedSignupFlow.whitelistRegex.regex}${
                        selectedSignupFlow.whitelistRegex.flags
                          ? ` | Flags: ${selectedSignupFlow.whitelistRegex.flags}`
                          : ''
                      }`}
                      target={
                        <div className="flex items-center">
                          <Typography.Paragraph3>Regex</Typography.Paragraph3>
                          <Icon icon={['fas', 'circle-info']} />
                        </div>
                      }
                    />
                  )}
                  {selectedSignupFlow.whitelistGreenhouseJobIds &&
                    !!selectedSignupFlow.whitelistGreenhouseJobIds.length && (
                      <Tooltip
                        text={`${selectedSignupFlow.whitelistGreenhouseJobIds.join(', ')}${
                          selectedSignupFlow.whitelistGreenhouseJobStageNames?.length
                            ? ` | Stages: ${selectedSignupFlow.whitelistGreenhouseJobStageNames.join(
                                ', ',
                              )}`
                            : '| Stages: Offer'
                        }`}
                        target={
                          <div className="flex items-center">
                            <Typography.Paragraph3>Greenhouse Job IDs</Typography.Paragraph3>
                            <Icon icon={['fas', 'circle-info']} />
                          </div>
                        }
                      />
                    )}
                </div>
              </>
            )}
            <div className="col-span-2 flex items-center pr-2">
              <Typography.Paragraph3 className="col-span-2">
                Apply Signup Flow To User ID
              </Typography.Paragraph3>
              <Tooltip
                text={
                  'If a signup flow did not run for a user, you can re-apply this signup flow to a user. Ensure they are added to any necessary whitelists.'
                }
                target={
                  <div>
                    <Icon icon={['fas', 'circle-info']} />
                  </div>
                }
                side="bottom"
              />
            </div>
            <div className="col-span-5 flex items-center gap-2">
              <TextField
                className="w-56"
                value={manualUserId}
                onChange={e => setManualUserId(e.target.value)}
                placeholder="User ID"
              />
              <Button variant="primary" onClick={manuallyApplyFlow} disabled={!manualUserId}>
                Apply
              </Button>
            </div>
            <Typography.Paragraph3 className="col-span-2">
              All-Time User Count
            </Typography.Paragraph3>
            <Typography.Paragraph3 className="col-span-5">
              {signupFlowQuery.data.userCount}
            </Typography.Paragraph3>
          </div>
        </>
      )}
    </PanelEntry>
  );
};
export default DetailPanelSignupFlow;
